import { translations } from './translations';
import { formatLocale } from './utils';
import { TranslationConfig } from './typings';

const DEFAULT_LOCALE = 'en-US';

export class I18n<T> {
    static instance: I18n<any>;
    private translations!: T;
    private _cache: { [x: string]: T } = (translations || {}) as any;
    private _config: TranslationConfig = { locale: DEFAULT_LOCALE, async: false };
    loadFile: (locale: string) => Promise<any>;

    constructor(locale: string = DEFAULT_LOCALE) {
        this.loadFile = () => Promise.resolve(null);
        this.setLocale(locale);
    }

    static getInstance<T>() {
        if (!I18n.instance) {
            I18n.instance = new I18n<T>();
        }
        return I18n.instance;
    }

    public init(config: TranslationConfig) {
        this._config = config;
        if(config.loadFile) {
            this.loadFile = config.loadFile;
        }
        if (config.locale) {
            this.setLocale(config.locale);
        }
    }
    
    public async setLocale(locale: string) {
        const formattedLocale = formatLocale(locale);
        this._config.locale = formattedLocale;
        this.translations = await this.loadLocale(formattedLocale) as T;  
    }

    public getLocale() {
        return this._config.locale;
    }

    public t(key: string, args?: { [x: string]: string }) {
        let translation = (key.split(".").reduce((obj, i) => obj?.[i], this.translations as any) as string) || key;
        if (typeof translation === 'string') {
            return translation.replace(/{(\w+)}/g, (match, arg) => {
                let value = args?.[arg];
                if (!value && match === `{appName}`) {
                    value = this._config.appName;
                }
                return value || match;
            });
        }
        return key;
    }

    private async loadLocale(locale: string): Promise<T> {
        if (!this._cache[locale] && this._config.async) {
            let data;
            try {
                data = await this.loadFile(locale);
            } catch (error) {
                /* ignore error */
            }
            if (!data) { // fallback to default locale
                data = await this.loadLocale(DEFAULT_LOCALE);
            }
            this._cache[locale] = data;
        }
        return this._cache[locale] || this._cache[DEFAULT_LOCALE];
    }
}
  
export const i18n = I18n.getInstance();
export default i18n;